import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';

import en from './en.json';
import ro from './ro.json';
import de from './de.json';
import es from './es.json';
import fr from './fr.json';

type Locale = 'en' | 'ro' | 'de' | 'es' | 'fr';

type Messages = Record<string, string>;

interface I18nContextValue {
  locale: Locale;
  t: (key: string, params?: Record<string, string | number>) => string;
  setLocale: (locale: Locale) => void;
}

const I18N_STORAGE_KEY = 'app_locale';

const dictionaries: Record<Locale, Messages> = {
  en,
  ro,
  de,
  es,
  fr,
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return Object.keys(params).reduce((acc, key) => {
    const value = String(params[key]);
    return acc.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), value);
  }, template);
}

function getNested(messages: Messages, key: string): string | undefined {
  // We store keys flat in JSON, but support dot-notation lookups.
  if (messages[key] !== undefined) return messages[key];
  return undefined;
}

function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') {
    return 'en';
  }

  try {
    const stored = window.localStorage.getItem(I18N_STORAGE_KEY) as Locale | null;
    if (stored && ['en', 'ro', 'de', 'es', 'fr'].includes(stored)) {
      return stored;
    }
  } catch {
    // ignore storage errors
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    const lang = navigator.language.toLowerCase();
    if (lang.startsWith('ro')) return 'ro';
    if (lang.startsWith('de')) return 'de';
    if (lang.startsWith('es')) return 'es';
    if (lang.startsWith('fr')) return 'fr';
  }

  return 'en';
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => detectInitialLocale());

  useEffect(() => {
    try {
      window.localStorage.setItem(I18N_STORAGE_KEY, locale);
    } catch {
      // ignore storage errors
    }
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    const currentMessages = dictionaries[locale] || dictionaries.en;

    const translate = (key: string, params?: Record<string, string | number>): string => {
      const fromCurrent = getNested(currentMessages, key);
      if (fromCurrent !== undefined) {
        return interpolate(fromCurrent, params);
      }

      const fallback = getNested(dictionaries.en, key);
      if (fallback !== undefined) {
        return interpolate(fallback, params);
      }

      // As a last resort, surface the key so missing translations are obvious.
      return key;
    };

    const setLocale = (next: Locale) => {
      setLocaleState(next);
    };

    return {
      locale,
      t: translate,
      setLocale,
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return ctx;
}

